import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const categoryLabels = {
  undervisning: 'Undervisning',
  skolehverdag: 'Skolehverdag',
  optagelse: 'Optagelse',
  arrangementer: 'Arrangementer'
};

export default function NewsList() {
  const [newsItems, setNewsItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/_api/public/content/nyheder')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente nyheder');
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
          const sorted = [...data].sort((a, b) => new Date(b.dato || b.date) - new Date(a.dato || a.date));
          const mapped = sorted.map((n) => {
            const rawCat = n.terms?.['nyhedskategori']?.[0] || n.kategori || n.category;
            const category = categoryLabels[rawCat] || rawCat || '';
            return {
              id: n.id,
              url_slug: n.url_slug,
              title: n.titel || n.title,
              summary: n.resume || n.summary,
              date: n.dato || n.date,
              image: n.billede || n.image,
              category
            };
          });
          setNewsItems(mapped);
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  const categories = ['Alle', ...new Set(newsItems.map((n) => n.category).filter(Boolean))];

  const filteredNews = selectedCategory === 'Alle'
    ? newsItems
    : newsItems.filter((n) => n.category === selectedCategory);

  return (
    <div className="bg-white pb-20">
      
      {/* Header Banner following container line */}
      <section className="bg-[#eef4e6] border-b border-[#d9e5d4] py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1D2923] tracking-tight font-serif-heading">
              Nyheder & Aktiviteter
            </h1>
            <p className="text-base sm:text-lg text-[#5A6961] leading-relaxed">
              Følg med i hverdagen, arrangementer og fællesskabet på Odder Lille Friskole.
            </p>
          </div>
        </div>
      </section>

      {/* Main List following container line */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 lg:pt-14">
        
        {/* Category Filters */}
        {categories.length > 1 && (
          <div className="flex flex-wrap items-center gap-2 mb-10">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all cursor-pointer ${
                  selectedCategory === cat
                    ? 'bg-[#264A35] text-white shadow-xs'
                    : 'bg-white text-[#2D3A32] border border-[#d9e5d4] hover:bg-[#eef4e6]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-16">
            <i className="fa-solid fa-spinner fa-spin text-2xl text-[#264A35]" aria-hidden="true" />
            <p className="text-sm text-[#5A6961] mt-2">Indlæser nyheder...</p>
          </div>
        ) : filteredNews.length === 0 ? (
          <div className="text-center py-16 text-[#5A6961]">
            Ingen nyheder fundet i denne kategori.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredNews.map((item) => (
              <article
                key={item.id || item.url_slug}
                className="bg-white rounded-3xl border border-[#d9e5d4] overflow-hidden shadow-xs hover:shadow-md transition-all flex flex-col justify-between group"
              >
                <div>
                  {item.image && item.image[0] && (
                    <div className="h-56 overflow-hidden bg-[#eef4e6]">
                      <img
                        src={item.image[0]}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <h2 className="text-xl font-bold text-[#1D2923] font-serif-heading mb-3 group-hover:text-[#264A35] transition-colors leading-snug">
                      {item.title}
                    </h2>
                    <p className="text-sm text-[#5A6961] leading-relaxed line-clamp-3">
                      {item.summary}
                    </p>
                  </div>
                </div>

                <div className="px-6 pb-6 pt-2">
                  <Link
                    to={`/nyheder/${item.url_slug}`}
                    className="inline-flex items-center gap-2 text-sm font-bold text-[#264A35] group-hover:text-black transition-colors"
                  >
                    <span>Læs hele nyheden</span>
                    <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}

      </section>

    </div>
  );
}
